import React from 'react';
import ReactDOM from 'react-dom';
import { BoardSearchOsFuturaPeriodPanel } from './BoardSearchOsFuturaPeriodPanel';
import { getFilterWidthHint } from './popoverWidthUtils';
import { BoardSearchSimplePickerPanel } from './BoardSearchSimplePickerPanel';
import { sortByCountDesc } from './filterChipLayout';

export function BoardSearchTechnicianPrevisaoPanel({
  previsaoWithCounts,
  tempFilters,
  setTempFilters,
  applyFilters,
  clearFilters,
  onApplyLive,
}) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const items = React.useMemo(() => {
    const base = sortByCountDesc(previsaoWithCounts);
    if (!searchTerm) return base;
    const t = searchTerm.toLowerCase();
    return base.filter((i) => i.previsao.toLowerCase().includes(t));
  }, [previsaoWithCounts, searchTerm]);

  const keys = items.map((i) => i.previsao);
  const selected = tempFilters.previsao || [];
  const availableKeysKey = React.useMemo(
    () => (previsaoWithCounts || []).map((i) => i.previsao).join('\0'),
    [previsaoWithCounts]
  );

  const commit = React.useCallback(
    (next) => {
      setTempFilters(next);
      onApplyLive?.(next);
    },
    [setTempFilters, onApplyLive]
  );

  // Com opções listadas e nada marcado: aplicar todas (modo subtrativo)
  React.useEffect(() => {
    const availableKeys = availableKeysKey ? availableKeysKey.split('\0') : [];
    if (!availableKeys.length || (tempFilters.previsao || []).length > 0) return;
    commit({ ...tempFilters, previsao: availableKeys });
  }, [availableKeysKey, tempFilters, commit]);

  const toggle = (item) => {
    const previsao = item.previsao;
    const cur = tempFilters.previsao || [];
    // Não deixar lista vazia enquanto houver opções (sempre aplicado)
    if (cur.includes(previsao) && cur.length === 1 && keys.includes(previsao)) {
      return;
    }
    commit({
      ...tempFilters,
      previsao: cur.includes(previsao) ? cur.filter((p) => p !== previsao) : [...cur, previsao],
    });
  };

  const handleBulkToggle = () => {
    const allMarked = keys.length > 0 && keys.every((k) => selected.includes(k));
    if (allMarked) {
      // Mantém ao menos a opção com maior contagem (lista já ordenada por count)
      commit({ ...tempFilters, previsao: keys.length ? [keys[0]] : [] });
      return;
    }
    commit({ ...tempFilters, previsao: Array.from(new Set([...selected, ...keys])) });
  };

  return (
    <BoardSearchSimplePickerPanel
      searchPlaceholder="Buscar"
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      items={items}
      getKey={(i) => i.previsao}
      getLabel={(i) => i.previsao}
      getSuffix={(i) => i.count}
      selectedKeys={selected}
      onToggle={toggle}
      emptyMessage="Nenhuma previsão encontrada"
      listAriaLabel="Previsões"
      bulkMenuEnabled={keys.length > 1}
      onBulkToggleAll={keys.length > 1 ? handleBulkToggle : undefined}
    />
  );
}

export function BoardSearchTechnicianRetornoPanel({
  tempFilters,
  setTempFilters,
  retornoCounts = {},
  onApplyLive,
}) {
  const ret = tempFilters.retorno;
  const { aguardando = 0, liberado = 0 } = retornoCounts;

  const commit = (next) => {
    setTempFilters(next);
    onApplyLive?.(next);
  };

  const items = [
    {
      key: 'aguardando',
      label: 'Retornos (aguardando)',
      count: aguardando,
      isDisabled: ret === 'nao',
      isSelected: ret && ret !== 'nao' && !!ret.aguardando,
      onToggle: () => {
        if (ret === 'nao') return;
        const prev = ret && ret !== 'nao' ? ret : {};
        const nextAguardando = !prev.aguardando;
        if (!nextAguardando && !prev.liberado) commit({ ...tempFilters, retorno: null });
        else commit({ ...tempFilters, retorno: { ...prev, aguardando: nextAguardando } });
      },
    },
    {
      key: 'liberado',
      label: 'Retornos (liberado)',
      count: liberado,
      isDisabled: ret === 'nao',
      isSelected: ret && ret !== 'nao' && !!ret.liberado,
      onToggle: () => {
        if (ret === 'nao') return;
        const prev = ret && ret !== 'nao' ? ret : {};
        const nextLiberado = !prev.liberado;
        if (!nextLiberado && !prev.aguardando) commit({ ...tempFilters, retorno: null });
        else commit({ ...tempFilters, retorno: { ...prev, liberado: nextLiberado } });
      },
    },
    {
      key: 'nao',
      label: 'Não exibir retorno',
      count: null,
      isDisabled: false,
      isSelected: ret === 'nao',
      onToggle: () => {
        commit({ ...tempFilters, retorno: ret === 'nao' ? null : 'nao' });
      },
    },
  ];

  return (
    <BoardSearchSimplePickerPanel
      showSearch={false}
      items={sortByCountDesc(items.filter((i) => i.count == null || i.count > 0))}
      getKey={(i) => i.key}
      getLabel={(i) => i.label}
      getSuffix={(i) => i.count}
      isItemDisabled={(i) => i.isDisabled}
      isSelected={(i) => i.isSelected}
      onToggle={(i) => i.onToggle()}
      emptyMessage="Nenhuma opção"
      listAriaLabel="Retorno"
    />
  );
}

export function BoardSearchTechnicianOsFuturaPanel({
  anchorRect,
  futuraOrders = [],
  tempFilters,
  onClose,
}) {
  if (!anchorRect) return null;

  const viewportPad = 8;
  const width = getFilterWidthHint('osFutura');
  let left = anchorRect.right - width;
  if (left < viewportPad) left = viewportPad;
  if (left + width > window.innerWidth - viewportPad) {
    left = window.innerWidth - width - viewportPad;
  }

  const style = {
    position: 'fixed',
    top: anchorRect.bottom + 4,
    left,
    width,
    zIndex: 1110,
    boxSizing: 'border-box',
  };

  return ReactDOM.createPortal(
    <div
      className="board-search-filters-popover board-search-filters-popover--osFutura board-search-filters-popover--simple"
      style={style}
      role="dialog"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="filter-modal filter-modal--main bsf-popover-modal bsf-cidade-picker">
        <div className="filter-modal-content bsf-popover-modal-content bsf-cidade-picker-content">
          <BoardSearchOsFuturaPeriodPanel
            orders={futuraOrders}
            currentFilter={tempFilters.osFuturaRange}
            onApply={(startDate, endDate) => {
              onClose({ ...tempFilters, osFuturaRange: { startDate, endDate } }, true);
            }}
            onClear={() => {
              onClose({ ...tempFilters, osFuturaRange: null }, true);
            }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
